/**
 * People IQ — Employee Detail (Profile) page.
 * Tabs: Personal · Employment · Statutory · Audit
 *
 * Sensitive fields are already masked server-side for callers without the
 * `view_employee_sensitive` capability, so we render whatever the API sends.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ManageDocTypesDialog, DOC_LABELS } from "@/components/DocTypeManager";
import { useAuth } from "@/context/AuthContext";
import { hasCapability, formatDate, formatDateTime, formatINR, isAdminRole } from "@/lib/permissions";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Pencil, Lock, Unlock, RotateCcw, FileClock, User, Briefcase, Landmark, FileText, Users2, HeartHandshake, Upload, Trash2, Download, Camera, PlayCircle, CheckCircle2, XCircle, Package, LogOut as LogOutIcon, ClipboardCheck, IdCard, Send, Eye, PhoneCall, IndianRupee, Settings2 } from "lucide-react";

const LIFECYCLE = ["candidate", "active", "probation", "confirmed", "transferred", "suspended", "resigned", "terminated", "archived"];

export default function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [emp, setEmp] = useState(null);
  const [dept, setDept] = useState(null);
  const [desig, setDesig] = useState(null);
  const [audit, setAudit] = useState([]);
  const [tab, setTab] = useState("personal");
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [unlockReason, setUnlockReason] = useState("");
  const [lifecycleOpen, setLifecycleOpen] = useState(false);
  const [newState, setNewState] = useState("");
  const [lifecycleReason, setLifecycleReason] = useState("");
  const [assignCodeOpen, setAssignCodeOpen] = useState(false);
  const [assignCodeForm, setAssignCodeForm] = useState({ employee_code: "", lifecycle_state: "active" });
  const [loading, setLoading] = useState(true);

  const canEdit = hasCapability(user, "edit_employee");
  const canFinalize = hasCapability(user, "finalize_employee");
  const canUnlock = hasCapability(user, "unlock_finalized_employee");
  const canLifecycle = hasCapability(user, "change_lifecycle_state");
  const canAudit = hasCapability(user, "view_employee_audit");
  const canViewSalary = hasCapability(user, "view_employee_salary");
  const canManageSalary = hasCapability(user, "manage_employee_salary");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/people/employees/${id}`);
      setEmp(data);
      const [{ data: depts }, { data: desigs }] = await Promise.all([
        api.get("/people/org/departments"),
        api.get("/people/org/designations"),
      ]);
      setDept(depts.find((d) => d.id === data.department_id));
      setDesig(desigs.find((d) => d.id === data.designation_id));
      if (canAudit) {
        const { data: a } = await api.get(`/people/employees/${id}/audit`);
        setAudit(a);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load employee");
      navigate("/people");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const doFinalize = async () => {
    if (!window.confirm("Finalize this record? Legal identity fields will be locked from further edits.")) return;
    try {
      await api.post(`/people/employees/${id}/finalize`);
      toast.success("Record finalized");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to finalize"); }
  };

  const doUnlock = async () => {
    try {
      await api.post(`/people/employees/${id}/unlock`, { reason: unlockReason });
      toast.success("Record unlocked");
      setUnlockOpen(false); setUnlockReason("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to unlock"); }
  };

  const doLifecycle = async () => {
    try {
      await api.post(`/people/employees/${id}/lifecycle`, {
        new_state: newState,
        reason: lifecycleReason || null,
      });
      toast.success(`Moved to ${newState}`);
      setLifecycleOpen(false); setNewState(""); setLifecycleReason("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to change state"); }
  };

  if (loading || !emp) return <div className="text-slate-400 text-sm">Loading employee…</div>;

  const printIdCard = async (eid) => {
    try {
      const token = localStorage.getItem("assetiq_token");
      const res = await fetch(`${api.defaults.baseURL}/people/employees/${eid}/id-card.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("ID card generation failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const w = window.open(url, "_blank");
      if (!w) { // popup blocked → force download
        const a = document.createElement("a"); a.href = url;
        a.download = `id-card-${emp.employee_code}.pdf`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
      }
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error(e.message || "Failed"); }
  };

  return (
    <div className="space-y-5" data-testid="employee-detail-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/people")}><ArrowLeft className="w-4 h-4" /></Button>
          <PhotographAvatar employeeId={emp.id} hasPhotograph={!!emp.photograph_storage_path} canEdit={canEdit} onUploaded={load} name={emp.full_name} />
          <div>
            <div className="text-xs font-semibold tracking-widest uppercase text-[#7C1D3A]">People IQ</div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 mt-0.5 flex items-center gap-2">
              {emp.full_name}
              <Badge variant="outline" className="font-mono text-xs">{emp.employee_code}</Badge>
              {emp.is_finalized && <Badge className="bg-blue-100 text-blue-700"><Lock className="w-3 h-3 mr-1" /> Locked</Badge>}
              <Badge className="bg-slate-100 text-slate-700 capitalize">{emp.lifecycle_state}</Badge>
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {desig?.name || "—"} · {dept?.name || "—"}
              {emp.date_of_joining && <> · Joined {formatDate(emp.date_of_joining)}</>}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!emp.employee_code && canEdit && (
            <Button variant="outline" size="sm" className="border-emerald-400 text-emerald-700 hover:bg-emerald-50" onClick={() => setAssignCodeOpen(true)} data-testid="assign-code-btn">
              <IdCard className="w-4 h-4 mr-1.5" /> Assign Code
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => printIdCard(emp.id)} data-testid="id-card-btn" disabled={!emp.employee_code}>
            <IdCard className="w-4 h-4 mr-1.5" /> ID Card
          </Button>
          {canLifecycle && (
            <Button variant="outline" size="sm" onClick={() => setLifecycleOpen(true)} data-testid="lifecycle-btn">
              <RotateCcw className="w-4 h-4 mr-1.5" /> Change State
            </Button>
          )}
          {canEdit && (
            <Button variant="outline" size="sm" onClick={() => navigate(`/people/${id}/edit`)} data-testid="edit-emp-btn">
              <Pencil className="w-4 h-4 mr-1.5" /> Edit
            </Button>
          )}
          {!emp.is_finalized && canFinalize && (
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={doFinalize} data-testid="finalize-btn">
              <Lock className="w-4 h-4 mr-1.5" /> Finalize
            </Button>
          )}
          {emp.is_finalized && canUnlock && (
            <Button size="sm" variant="outline" className="border-amber-400 text-amber-700" onClick={() => setUnlockOpen(true)} data-testid="unlock-btn">
              <Unlock className="w-4 h-4 mr-1.5" /> Unlock
            </Button>
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="emp-tabs" className="flex-wrap h-auto">
          <TabsTrigger value="personal" data-testid="tab-personal"><User className="w-3.5 h-3.5 mr-1" /> Personal</TabsTrigger>
          <TabsTrigger value="employment" data-testid="tab-employment"><Briefcase className="w-3.5 h-3.5 mr-1" /> Employment</TabsTrigger>
          <TabsTrigger value="statutory" data-testid="tab-statutory"><Landmark className="w-3.5 h-3.5 mr-1" /> Statutory</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents"><FileText className="w-3.5 h-3.5 mr-1" /> Documents</TabsTrigger>
          <TabsTrigger value="dependents" data-testid="tab-dependents"><Users2 className="w-3.5 h-3.5 mr-1" /> Dependents</TabsTrigger>
          <TabsTrigger value="nominees" data-testid="tab-nominees"><HeartHandshake className="w-3.5 h-3.5 mr-1" /> Nominee (for the purpose of EPFO)</TabsTrigger>
          <TabsTrigger value="emergency" data-testid="tab-emergency"><PhoneCall className="w-3.5 h-3.5 mr-1" /> Emergency Contact</TabsTrigger>
          <TabsTrigger value="assets" data-testid="tab-assets"><Package className="w-3.5 h-3.5 mr-1" /> Assets</TabsTrigger>
          <TabsTrigger value="onboarding" data-testid="tab-onboarding"><ClipboardCheck className="w-3.5 h-3.5 mr-1" /> Onboarding</TabsTrigger>
          <TabsTrigger value="offboarding" data-testid="tab-offboarding"><LogOutIcon className="w-3.5 h-3.5 mr-1" /> Offboarding</TabsTrigger>
          {canViewSalary && <TabsTrigger value="salary" data-testid="tab-salary"><IndianRupee className="w-3.5 h-3.5 mr-1" /> Salary</TabsTrigger>}
          {canAudit && <TabsTrigger value="audit" data-testid="tab-audit"><FileClock className="w-3.5 h-3.5 mr-1" /> Audit</TabsTrigger>}
        </TabsList>

        <TabsContent value="personal" className="mt-4">
          <Card><CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-6">
            <Info label="Full name">{emp.full_name}</Info>
            <Info label="Preferred name">{emp.preferred_name || "—"}</Info>
            <Info label="Date of birth">{formatDate(emp.date_of_birth)}</Info>
            <Info label="Gender">{emp.gender || "—"}</Info>
            <Info label="Marital status">{emp.marital_status || "—"}</Info>
            <Info label="Blood group">{emp.blood_group || "—"}</Info>
            <Info label="Personal email">{emp.personal_email || "—"}</Info>
            <Info label="Personal mobile">{emp.personal_mobile || "—"}</Info>
            <Info label="Official email">{emp.official_email || "—"}</Info>
            <Info label="Official mobile">{emp.official_mobile || "—"}</Info>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="employment" className="mt-4">
          <Card><CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-6">
            <Info label="Date of joining">{formatDate(emp.date_of_joining)}</Info>
            <Info label="Department">{dept?.name || "—"}</Info>
            <Info label="Designation">{desig?.name || "—"}</Info>
            <Info label="Location">{emp.location || "—"}</Info>
            <Info label="Cost center">{emp.cost_center || "—"}</Info>
            <Info label="Grade / band">{emp.grade_band || "—"}</Info>
            <Info label="Employment type">{(emp.employment_type || "").replace(/_/g, " ") || "—"}</Info>
            <Info label="Lifecycle state">{emp.lifecycle_state}</Info>
            <Info label="Finalized">{emp.is_finalized ? `Yes · ${formatDateTime(emp.finalized_at)}` : "No"}</Info>
          </CardContent></Card>
          {emp.lifecycle_history && emp.lifecycle_history.length > 1 && (
            <Card className="mt-4">
              <CardHeader><CardTitle className="text-sm">Lifecycle history</CardTitle></CardHeader>
              <CardContent className="text-xs space-y-1">
                {emp.lifecycle_history.map((h, i) => (
                  <div key={i} className="flex items-start gap-3 border-b border-slate-100 py-1.5">
                    <Badge className="bg-slate-100 text-slate-700 capitalize">{h.state}</Badge>
                    <span className="text-slate-600">{formatDateTime(h.from_ts)}{h.to_ts && <> → {formatDateTime(h.to_ts)}</>}</span>
                    {h.reason && <span className="text-slate-500 italic">— {h.reason}</span>}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="statutory" className="mt-4">
          <Card><CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-6">
            <Info label="PAN" sensitive>{emp.pan || "—"}</Info>
            <Info label="Aadhaar" sensitive>{emp.aadhaar || "—"}</Info>
            <Info label="Voter ID">{emp.voter_id || "—"}</Info>
            <Info label="Driving license">{emp.driving_license || "—"}</Info>
            <Info label="ESIC number">{emp.esic_applicable ? (emp.esic_number || "—") : <span className="text-slate-400 italic">Not applicable</span>}</Info>
            <Info label="PF / UAN Number">{emp.pf_applicable ? (emp.uan_number || "—") : <span className="text-slate-400 italic">Not applicable</span>}</Info>
            <Info label="Bank account" sensitive>{emp.bank_account_number || "—"}</Info>
            <Info label="Bank IFSC">{emp.bank_ifsc || "—"}</Info>
            <Info label="Bank name">{emp.bank_name || "—"}</Info>
          </CardContent></Card>
          {!hasCapability(user, "view_employee_sensitive") && (
            <div className="mt-3 text-xs text-slate-500 italic">
              PAN, Aadhaar & bank account are masked for your role. HR / Admin can see the full values.
            </div>
          )}
        </TabsContent>

        <TabsContent value="documents" className="mt-4">
          <DocumentsPanel employeeId={id} employee={emp} />
        </TabsContent>

        <TabsContent value="dependents" className="mt-4">
          <DependentsPanel employeeId={id} employee={emp} />
        </TabsContent>

        <TabsContent value="nominees" className="mt-4">
          <NomineesPanel employeeId={id} employee={emp} />
        </TabsContent>

        <TabsContent value="emergency" className="mt-4">
          <EmergencyContactPanel employee={emp} />
        </TabsContent>

        <TabsContent value="assets" className="mt-4">
          <AssetsPanel employeeId={id} employee={emp} />
        </TabsContent>

        <TabsContent value="onboarding" className="mt-4">
          <OnboardingPanel employeeId={id} onDone={load} />
        </TabsContent>

        <TabsContent value="offboarding" className="mt-4">
          <OffboardingPanel employeeId={id} onDone={load} />
        </TabsContent>

        {canViewSalary && (
          <TabsContent value="salary" className="mt-4">
            <SalaryPanel employeeId={id} canManage={canManageSalary} />
          </TabsContent>
        )}

        {canAudit && (
          <TabsContent value="audit" className="mt-4">
            <Card><CardContent className="pt-6">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="audit-table">
                  <thead className="text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                    <tr>
                      <th className="text-left px-3 py-2">When</th>
                      <th className="text-left px-3 py-2">Action</th>
                      <th className="text-left px-3 py-2">By</th>
                      <th className="text-left px-3 py-2">Reason / Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audit.length === 0 && <tr><td colSpan="4" className="text-center text-slate-400 py-8">No entries.</td></tr>}
                    {audit.map((a) => (
                      <tr key={a.id} className="border-t border-slate-100">
                        <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">{formatDateTime(a.at)}</td>
                        <td className="px-3 py-2 text-xs font-mono">{a.action}</td>
                        <td className="px-3 py-2 text-xs">{a.actor_email} <span className="text-slate-400">({a.actor_role})</span></td>
                        <td className="px-3 py-2 text-xs text-slate-700">{a.reason || (a.duplicate_override ? "Duplicate override" : "—")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent></Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Unlock dialog */}
      <Dialog open={unlockOpen} onOpenChange={setUnlockOpen}>
        <DialogContent data-testid="unlock-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Unlock className="w-5 h-5 text-amber-600" /> Unlock finalized record</DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-3">
            <div className="rounded bg-amber-50 border border-amber-200 text-amber-900 text-xs p-2">
              Unlocking is audited. Legal identity fields become editable again until the record is re-finalized.
            </div>
            <Label className="text-xs">Reason (required, ≥ 5 chars)</Label>
            <Textarea rows={3} value={unlockReason} onChange={(e) => setUnlockReason(e.target.value)} data-testid="unlock-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnlockOpen(false)}>Cancel</Button>
            <Button disabled={unlockReason.trim().length < 5} onClick={doUnlock} className="bg-amber-600 hover:bg-amber-700" data-testid="unlock-confirm">Unlock</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign code dialog (candidate → hired) */}
      <Dialog open={assignCodeOpen} onOpenChange={setAssignCodeOpen}>
        <DialogContent data-testid="assign-code-dialog">
          <DialogHeader><DialogTitle>Assign Employee Code</DialogTitle></DialogHeader>
          <div className="text-sm space-y-3">
            <div className="rounded bg-emerald-50 border border-emerald-200 text-emerald-900 text-xs p-2">
              Use this once <b>{emp.full_name}</b> has physically joined. The employee code cannot be changed later.
            </div>
            <Label className="text-xs">Employee code (numeric)</Label>
            <input
              className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm font-mono"
              value={assignCodeForm.employee_code}
              onChange={(e) => setAssignCodeForm({ ...assignCodeForm, employee_code: e.target.value.replace(/[^\d]/g, "") })}
              data-testid="assign-code-input"
              placeholder="e.g. 1001"
            />
            <Label className="text-xs">New lifecycle state</Label>
            <Select value={assignCodeForm.lifecycle_state} onValueChange={(v) => setAssignCodeForm({ ...assignCodeForm, lifecycle_state: v })}>
              <SelectTrigger data-testid="assign-code-state"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="probation">Probation</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignCodeOpen(false)}>Cancel</Button>
            <Button
              disabled={!assignCodeForm.employee_code}
              onClick={async () => {
                try {
                  await api.post(`/people/employees/${id}/assign-code`, assignCodeForm);
                  toast.success(`Assigned code ${assignCodeForm.employee_code}`);
                  setAssignCodeOpen(false);
                  setAssignCodeForm({ employee_code: "", lifecycle_state: "active" });
                  load();
                } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
              }}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="assign-code-confirm"
            >Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lifecycle dialog */}
      <Dialog open={lifecycleOpen} onOpenChange={setLifecycleOpen}>
        <DialogContent data-testid="lifecycle-dialog">
          <DialogHeader><DialogTitle>Change Lifecycle State</DialogTitle></DialogHeader>
          <div className="text-sm space-y-3">
            <div className="text-xs text-slate-500">Current: <b className="capitalize">{emp.lifecycle_state}</b></div>
            <Label className="text-xs">New state</Label>
            <Select value={newState} onValueChange={setNewState}>
              <SelectTrigger data-testid="lifecycle-new-state"><SelectValue placeholder="Pick new state" /></SelectTrigger>
              <SelectContent>
                {LIFECYCLE.filter((s) => s !== emp.lifecycle_state).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Label className="text-xs">Reason (optional)</Label>
            <Textarea rows={2} value={lifecycleReason} onChange={(e) => setLifecycleReason(e.target.value)} data-testid="lifecycle-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLifecycleOpen(false)}>Cancel</Button>
            <Button disabled={!newState} onClick={doLifecycle} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="lifecycle-confirm">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, sensitive, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">
        {label}
        {sensitive && <Badge variant="outline" className="text-[9px] px-1 py-0 font-normal">sensitive</Badge>}
      </div>
      <div className="text-sm text-slate-900 mt-0.5 break-words">{children}</div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Photograph avatar (top-left of the profile header)
// -----------------------------------------------------------------------------
function PhotographAvatar({ employeeId, hasPhotograph, canEdit, onUploaded, name }) {
  const [busy, setBusy] = useState(false);
  const [enlarge, setEnlarge] = useState(false);
  const initials = (name || "").split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
  const src = hasPhotograph ? `${api.defaults.baseURL}/people/employees/${employeeId}/photograph?t=${Date.now()}` : null;

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Photo must be under 5MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const token = localStorage.getItem("assetiq_token");
      const res = await fetch(`${api.defaults.baseURL}/people/employees/${employeeId}/photograph`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Upload failed");
      toast.success("Photograph updated");
      onUploaded?.();
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally { setBusy(false); e.target.value = ""; }
  };

  // Indian passport-photo ratio (35 × 45 mm) — click to enlarge.
  return (
    <div className="relative group shrink-0">
      <div
        className={`w-[180px] h-[231px] rounded-md bg-slate-100 border border-slate-300 shadow-sm overflow-hidden flex items-center justify-center text-slate-400 font-heading font-bold text-3xl ${src ? "cursor-zoom-in" : ""}`}
        onClick={() => src && setEnlarge(true)}
        data-testid="photo-frame"
        title={src ? "Click to enlarge" : undefined}
      >
        {src ? (
          <img src={src} alt={name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        ) : (
          <span>{initials || "?"}</span>
        )}
      </div>
      {canEdit && (
        <label
          className="absolute inset-x-0 bottom-0 rounded-b-md flex items-center justify-center gap-1 bg-black/50 text-white text-[10px] py-1.5 opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity"
          data-testid="photo-upload-label"
        >
          {busy ? <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><Camera className="w-3.5 h-3.5" /> Change</>}
          <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="photo-upload-input" />
        </label>
      )}
      <Dialog open={enlarge} onOpenChange={setEnlarge}>
        <DialogContent className="max-w-md" data-testid="photo-enlarge-dialog">
          <DialogHeader><DialogTitle>{name}</DialogTitle></DialogHeader>
          {src && <img src={src} alt={name} className="w-full max-h-[70vh] object-contain rounded" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Documents panel
// -----------------------------------------------------------------------------
const DOC_TYPES = [  // fallback if the doc-type master API is unavailable
  "identity_proof", "address_proof", "education_proof", "nominee_proof",
  "dependent_proof", "bank_proof", "joining_document", "exit_document",
  "family_photograph", "esic_photograph", "other",
];

// Sep 2026 — EPFO/ESIC scheme documents are offered only when the employee is
// marked eligible on the employee record (the backend enforces the same).
const ESIC_DOC_KEYS = ["esic_photograph", "family_photograph"];
const EPFO_DOC_KEYS = ["nominee_proof"];

function DocumentsPanel({ employeeId, employee }) {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [type, setType] = useState("identity_proof");
  const [docTypes, setDocTypes] = useState(DOC_TYPES.map((n) => ({ name: n, is_confidential: ["identity_proof", "bank_proof"].includes(n) })));
  const [manageTypesOpen, setManageTypesOpen] = useState(false);
  const canUpload = hasCapability(user, "upload_employee_document");
  const canDownload = hasCapability(user, "download_employee_document");
  const canDelete = hasCapability(user, "delete_employee_document");
  const isAdminTier = isAdminRole(user?.role) || (user?.secondary_roles || []).some(isAdminRole);

  const visibleDocTypes = docTypes.filter((t) => {
    if (ESIC_DOC_KEYS.includes(t.name) && !employee?.esic_applicable) return false;
    if (EPFO_DOC_KEYS.includes(t.name) && !employee?.pf_applicable) return false;
    return true;
  });
  const effectiveType = visibleDocTypes.some((t) => t.name === type) ? type : (visibleDocTypes[0]?.name || "other");

  const loadTypes = async () => {
    try {
      const { data } = await api.get("/people/document-types");
      if (data.length) setDocTypes(data);
    } catch { /* keep fallback list */ }
  };
  useEffect(() => { loadTypes(); }, []);

  const load = async () => {
    try {
      const { data } = await api.get(`/people/employees/${employeeId}/documents`);
      setDocs(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [employeeId]);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("doc_type", effectiveType);
      const token = localStorage.getItem("assetiq_token");
      const res = await fetch(`${api.defaults.baseURL}/people/employees/${employeeId}/documents`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Upload failed");
      toast.success("Uploaded");
      load();
    } catch (err) { toast.error(err.message || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const download = async (d) => {
    try {
      const token = localStorage.getItem("assetiq_token");
      const res = await fetch(`${api.defaults.baseURL}/people/employees/${employeeId}/documents/${d.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = d.filename || "document";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(err.message); }
  };

  const del = async (d) => {
    if (!window.confirm(`Delete document "${d.title || d.filename}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/people/employees/${employeeId}/documents/${d.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  // In-window preview (Sep 2026). Backend decides view rights:
  // sensitive docs → employee self + HR/admin only; others → view_employees.
  const [preview, setPreview] = useState(null); // { d, url, ct }
  const openPreview = async (d) => {
    try {
      const token = localStorage.getItem("assetiq_token");
      const res = await fetch(`${api.defaults.baseURL}/people/employees/${employeeId}/documents/${d.id}/preview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 403) { toast.error("Confidential document — only the employee, HR and admins can view it"); return; }
      if (!res.ok) throw new Error("Preview failed");
      const blob = await res.blob();
      setPreview({ d, url: URL.createObjectURL(blob), ct: blob.type || d.content_type || "" });
    } catch (err) { toast.error(err.message || "Preview failed"); }
  };
  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  return (
    <Card><CardContent className="pt-6 space-y-4">
      {canUpload && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4" data-testid="doc-upload-block">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase font-semibold text-slate-500">Doc type</label>
              <div className="flex items-center gap-1.5 mt-1">
                <select className="flex-1 border border-slate-300 rounded px-2 py-1.5 text-sm" value={effectiveType} onChange={(e) => setType(e.target.value)} data-testid="doc-type-select">
                  {visibleDocTypes.map((t) => (
                    <option key={t.name} value={t.name}>
                      {DOC_LABELS[t.name] || t.name.replace(/_/g, " ")}{t.is_confidential ? " (confidential)" : ""}{t.is_mandatory ? " (mandatory)" : ""}
                    </option>
                  ))}
                </select>
                {isAdminTier && (
                  <button
                    type="button"
                    onClick={() => setManageTypesOpen(true)}
                    className="p-1.5 rounded border border-slate-300 text-slate-500 hover:text-[#7C1D3A] hover:border-[#7C1D3A]"
                    title="Manage document types (Admin)"
                    data-testid="manage-doc-types-btn"
                  >
                    <Settings2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-end gap-2 md:col-span-2">
              <label className="flex-1 mb-0 cursor-pointer">
                <span className="w-full block bg-[#7C1D3A] hover:bg-[#6c1c36] text-white text-sm rounded px-3 py-1.5 text-center" data-testid="doc-upload-btn">
                  <Upload className="w-3.5 h-3.5 inline mr-1" /> {uploading ? "Uploading…" : "Upload File"}
                </span>
                <input type="file" className="hidden" onChange={upload} data-testid="doc-upload-input" />
              </label>
            </div>
          </div>
          {(!employee?.esic_applicable || !employee?.pf_applicable) && (
            <p className="text-[11px] text-slate-400 mt-2" data-testid="doc-eligibility-note">
              {!employee?.esic_applicable && "ESIC document types (incl. Family Photo (For ESIC)) are hidden — this employee is not ESIC eligible. "}
              {!employee?.pf_applicable && "EPFO document types (Nominee Proof) are hidden — this employee is not PF eligible."}
            </p>
          )}
        </div>
      )}
      {!employee?.is_finalized && (() => {
        const missing = docTypes.filter((t) => t.is_mandatory && !docs.some((d) => d.doc_type === t.name));
        if (!missing.length) return null;
        return (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900" data-testid="mandatory-docs-missing">
            Mandatory before this record can be finalized: <b>{missing.map((t) => DOC_LABELS[t.name] || t.name.replace(/_/g, " ")).join(", ")}</b>
          </div>
        );
      })()}
      {docs.length === 0 ? (
        <div className="text-center py-10 text-slate-400 text-sm"><FileText className="w-8 h-8 mx-auto mb-1 opacity-50" /> No documents uploaded yet.</div>
      ) : (
        <table className="w-full text-sm" data-testid="doc-table">
          <thead className="text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
            <tr>
              <th className="text-left px-3 py-2">Document</th>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-left px-3 py-2">Uploaded by</th>
              <th className="text-left px-3 py-2">Size</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {docs.map((d) => (
              <tr key={d.id} className="border-t border-slate-100">
                <td className="px-3 py-2 font-medium text-slate-900">
                  {d.title || d.filename}
                  {d.is_sensitive && <Badge className="ml-2 bg-rose-100 text-rose-700 text-[9px] px-1 py-0">CONFIDENTIAL</Badge>}
                </td>
                <td className="px-3 py-2 text-xs text-slate-600 capitalize">{(d.doc_type || "").replace(/_/g, " ")}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{d.uploaded_by_name} <div className="text-[10px] text-slate-400">{formatDateTime(d.created_at)}</div></td>
                <td className="px-3 py-2 text-xs text-slate-600">{Math.round((d.size_bytes || 0) / 1024)} KB</td>
                <td className="px-3 py-2 text-right space-x-1">
                  <Button variant="ghost" size="icon" onClick={() => openPreview(d)} title="View" data-testid={`doc-view-${d.id}`}>
                    <Eye className="w-4 h-4" />
                  </Button>
                  {canDownload && (
                    <Button variant="ghost" size="icon" onClick={() => download(d)} data-testid={`doc-dl-${d.id}`}>
                      <Download className="w-4 h-4" />
                    </Button>
                  )}
                  {canDelete && (
                    <Button variant="ghost" size="icon" onClick={() => del(d)} className="text-rose-500 hover:text-rose-700" data-testid={`doc-del-${d.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <ManageDocTypesDialog
        open={manageTypesOpen}
        onClose={() => setManageTypesOpen(false)}
        docTypes={docTypes}
        onChanged={() => { loadTypes(); load(); }}
      />
      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) closePreview(); }}>
        <DialogContent className="max-w-3xl" data-testid="doc-preview-dialog">          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {preview?.d?.title || preview?.d?.filename}
              {preview?.d?.is_sensitive && <Badge className="bg-rose-100 text-rose-700 text-[9px] px-1 py-0">CONFIDENTIAL</Badge>}
            </DialogTitle>
          </DialogHeader>
          {preview && (
            preview.ct.startsWith("image/") ? (
              <img src={preview.url} alt={preview.d.filename} className="max-h-[70vh] w-auto mx-auto rounded" data-testid="doc-preview-image" />
            ) : preview.ct === "application/pdf" ? (
              <iframe src={preview.url} title={preview.d.filename} className="w-full h-[70vh] rounded border border-slate-200" data-testid="doc-preview-frame" />
            ) : (
              <div className="text-sm text-slate-500 py-8 text-center" data-testid="doc-preview-unsupported">
                Preview is available for PDF and image files only.
                {canDownload && (
                  <div className="mt-3">
                    <Button variant="outline" size="sm" onClick={() => { closePreview(); download(preview.d); }}>
                      <Download className="w-4 h-4 mr-1.5" /> Download instead
                    </Button>
                  </div>
                )}
              </div>
            )
          )}
        </DialogContent>
      </Dialog>
    </CardContent></Card>
  );
}

// -----------------------------------------------------------------------------
// Dependents panel
// -----------------------------------------------------------------------------
function DependentsPanel({ employeeId, employee }) {
  return <PeopleListPanel employeeId={employeeId} kind="dependents" testid="dependents" title="Dependents"
    icon={<Users2 className="w-4 h-4" />} employee={employee} />;
}

function NomineesPanel({ employeeId, employee }) {
  if (!employee?.pf_applicable) {
    return (
      <Card data-testid="nominees-not-applicable"><CardContent className="pt-6">
        <div className="text-sm text-slate-500 py-4">
          Nominee details are captured for <b>EPFO</b>. This employee is not marked <b>PF (EPFO) applicable</b> —
          enable it on the employee record to add nominees.
        </div>
      </CardContent></Card>
    );
  }
  return <PeopleListPanel employeeId={employeeId} kind="nominees" testid="nominees" title="Nominees (EPFO)"
    icon={<HeartHandshake className="w-4 h-4" />} employee={employee} />;
}

// -----------------------------------------------------------------------------
// Emergency Contact panel (Sep 2026) — READ-ONLY for HR/admin. Product
// decision: only the employee can change this data (from My Profile); HR is
// notified by email/WhatsApp on every change.
// -----------------------------------------------------------------------------
function EmergencyContactPanel({ employee }) {
  const current = employee.emergency_contact_name ? {
    name: employee.emergency_contact_name,
    relation: employee.emergency_contact_relation,
    phone: employee.emergency_contact_phone,
    updatedAt: employee.emergency_contact_updated_at,
    updatedBy: employee.emergency_contact_updated_by,
  } : null;

  return (
    <Card data-testid="emergency-panel"><CardContent className="pt-6 space-y-4">
      {current ? (
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-rose-50 text-rose-700 flex items-center justify-center"><PhoneCall className="w-5 h-5" /></div>
          <div>
            <div className="text-base font-semibold text-slate-900" data-testid="ec-current-name">{current.name}</div>
            <div className="text-sm text-slate-600 capitalize" data-testid="ec-current-relation">{current.relation}</div>
            <div className="text-sm font-mono text-slate-900" data-testid="ec-current-phone">{current.phone}</div>
            <div className="text-[11px] text-slate-400 mt-1">
              {current.updatedBy && <>Updated by {current.updatedBy}</>}
              {current.updatedAt && <> · {formatDateTime(current.updatedAt)}</>}
            </div>
          </div>
        </div>
      ) : (
        <div className="text-sm text-slate-400 py-2" data-testid="ec-empty">No emergency contact recorded yet.</div>
      )}
      <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded px-3 py-2" data-testid="ec-readonly-note">
        Only the employee can update their emergency contact (from My Profile). You are notified by email/WhatsApp whenever it changes.
      </div>
    </CardContent></Card>
  );
}

function PeopleListPanel({ employeeId, kind, title, icon, testid, employee }) {
  const { user } = useAuth();
  const canManage = hasCapability(user, "manage_employee_dependents");
  const [rows, setRows] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({});

  const emptyForm = kind === "nominees"
    ? { full_name: "", relationship: "", percentage: 100, scheme: "EPFO", date_of_birth: "", aadhaar: "", address: "", contact_number: "" }
    : { full_name: "", relationship: "", date_of_birth: "", gender: "", is_esic_dependent: false, aadhaar: "", notes: "" };
  // Sep 2026 — an "Others" nominee is allowed only when no family nominee exists.
  const familyNomineeExists = kind === "nominees" && rows.some((r) => ["spouse", "children", "dependent_parents"].includes(r.relationship));

  const load = async () => {
    try {
      const { data } = await api.get(`/people/employees/${employeeId}/${kind}`);
      setRows(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [employeeId, kind]);

  const submit = async () => {
    if (!form.full_name || !form.relationship) return toast.error("Name and relationship are required");
    try {
      await api.post(`/people/employees/${employeeId}/${kind}`, form);
      toast.success("Added");
      setAddOpen(false); setForm(emptyForm);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const del = async (r) => {
    if (!window.confirm(`Remove ${r.full_name}?`)) return;
    try {
      await api.delete(`/people/employees/${employeeId}/${kind}/${r.id}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  return (
    <Card>
      <CardHeader className="pb-3 flex-row items-center justify-between">
        <CardTitle className="text-sm flex items-center gap-2">{icon} {title}</CardTitle>
        {canManage && (
          <Button size="sm" onClick={() => { setForm(emptyForm); setAddOpen(true); }} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid={`add-${testid}-btn`}>
            + Add
          </Button>
        )}
      </CardHeader>
      <CardContent className="pt-0">
        {rows.length === 0 ? (
          <div className="text-center py-8 text-slate-400 text-sm">No {title.toLowerCase()} recorded.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
              <tr>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Relationship</th>
                {kind === "nominees" && <th className="text-left px-3 py-2">Scheme</th>}
                {kind === "nominees" && <th className="text-left px-3 py-2">%</th>}
                {kind === "dependents" && <th className="text-left px-3 py-2">DOB</th>}
                {kind === "dependents" && <th className="text-left px-3 py-2">ESIC</th>}
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody data-testid={`${testid}-tbody`}>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-medium text-slate-900">{r.full_name}</td>
                  <td className="px-3 py-2 text-slate-700 capitalize">{(r.relationship || "").replace(/_/g, " ")}</td>
                  {kind === "nominees" && <td className="px-3 py-2 text-xs text-slate-600">{r.scheme || "—"}</td>}
                  {kind === "nominees" && <td className="px-3 py-2 text-xs">{r.percentage}%</td>}
                  {kind === "dependents" && <td className="px-3 py-2 text-xs">{formatDate(r.date_of_birth)}</td>}
                  {kind === "dependents" && <td className="px-3 py-2 text-xs">{r.is_esic_dependent ? "Yes" : "—"}</td>}
                  <td className="px-3 py-2 text-right">
                    {canManage && (
                      <Button variant="ghost" size="icon" onClick={() => del(r)} className="text-rose-500 hover:text-rose-700">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid={`${testid}-dialog`}>
          <DialogHeader><DialogTitle>Add {kind === "nominees" ? "Nominee (EPFO)" : "Dependent"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Field2 label="Full name"><input value={form.full_name || ""} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid={`${testid}-name`} className="input" /></Field2>
            <Field2 label="Relationship">
              {kind === "nominees" ? (
                <select value={form.relationship || ""} onChange={(e) => setForm({ ...form, relationship: e.target.value })} data-testid={`${testid}-rel`} className="input">
                  <option value="">—</option>
                  <option value="spouse">Spouse</option>
                  <option value="children">Children</option>
                  <option value="dependent_parents">Dependent Parents</option>
                  <option value="others" disabled={familyNomineeExists}>Others (Only allowed if NO family exist)</option>
                </select>
              ) : (
                <input value={form.relationship || ""} onChange={(e) => setForm({ ...form, relationship: e.target.value })} data-testid={`${testid}-rel`} className="input" placeholder="spouse / son / …" />
              )}
            </Field2>
            <Field2 label="Date of birth"><input type="date" value={form.date_of_birth || ""} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} className="input" /></Field2>
            <Field2 label="Aadhaar"><input value={form.aadhaar || ""} onChange={(e) => setForm({ ...form, aadhaar: e.target.value.replace(/[^\d]/g, "") })} maxLength={12} className="input" /></Field2>
            {kind === "nominees" && <>
              <Field2 label="Scheme"><input value="EPFO" disabled className="input bg-slate-100 text-slate-500" data-testid="nominee-scheme" /></Field2>
              <Field2 label="Percentage"><input type="number" min={0} max={100} value={form.percentage ?? 100} onChange={(e) => setForm({ ...form, percentage: Number(e.target.value) })} className="input" /></Field2>
              <Field2 label="Contact"><input value={form.contact_number || ""} onChange={(e) => setForm({ ...form, contact_number: e.target.value })} className="input" /></Field2>
              <Field2 label="Address" span2><textarea rows={2} value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} className="input" /></Field2>
            </>}
            {kind === "dependents" && <>
              <Field2 label="Gender"><select value={form.gender || ""} onChange={(e) => setForm({ ...form, gender: e.target.value })} className="input"><option value="">—</option><option>male</option><option>female</option><option>other</option></select></Field2>
              <Field2 label="ESIC dependent">
                {employee?.esic_applicable ? (
                  <label className="flex items-center gap-2 h-9">
                    <input type="checkbox" checked={!!form.is_esic_dependent} onChange={(e) => setForm({ ...form, is_esic_dependent: e.target.checked })} data-testid="dependent-esic-check" /> yes
                  </label>
                ) : (
                  <div className="text-xs text-slate-400 italic h-9 flex items-center" data-testid="dependent-esic-na">Not applicable — employee is not ESIC eligible</div>
                )}
              </Field2>
              <Field2 label="Notes" span2><textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input" /></Field2>
            </>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={submit} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid={`${testid}-save`}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function Field2({ label, span2, children }) {
  return (
    <div className={span2 ? "col-span-2" : ""}>
      <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">{label}</label>
      <div className="mt-1 [&_.input]:w-full [&_.input]:border [&_.input]:border-slate-300 [&_.input]:rounded [&_.input]:px-2 [&_.input]:py-1.5 [&_.input]:text-sm">{children}</div>
    </div>
  );
}


// -----------------------------------------------------------------------------
// Assets panel — reads Asset IQ deployments linked to this employee
// -----------------------------------------------------------------------------
function AssetsPanel({ employeeId, employee }) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/people/employees/${employeeId}/assets`);
        setAssets(data);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
      finally { setLoading(false); }
    })();
  }, [employeeId]);

  return (
    <Card><CardContent className="pt-6">
      {!employee?.linked_user_id && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 mb-4">
          This employee is not yet linked to an Asset IQ user account. Assets deployed by the person-name
          fallback are still shown, but for the strongest match wire <b>linked_user_id</b> from the Personal
          tab (edit → link to Asset IQ user).
        </div>
      )}
      {loading ? (
        <div className="text-center text-slate-400 text-sm py-8">Loading…</div>
      ) : assets.length === 0 ? (
        <div className="text-center py-10 text-slate-400 text-sm">
          <Package className="w-8 h-8 mx-auto mb-1 opacity-50" /> No Asset IQ assets are currently deployed to this employee.
        </div>
      ) : (
        <table className="w-full text-sm" data-testid="assets-table">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Asset ID</th>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Category</th>
              <th className="text-left px-3 py-2">Location</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {assets.map((a) => (
              <tr key={a.id} className="border-t border-slate-100">
                <td className="px-3 py-2 font-mono text-xs">{a.asset_id}</td>
                <td className="px-3 py-2 font-medium">{a.name}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{a.category || "—"}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{a.location || "—"}</td>
                <td className="px-3 py-2 text-xs capitalize">{a.status}</td>
                <td className="px-3 py-2 text-right">
                  <Link to={`/assets/${a.id}`} className="text-[#7C1D3A] text-xs hover:underline">Open →</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </CardContent></Card>
  );
}

// -----------------------------------------------------------------------------
// Onboarding / Offboarding shared panel
// -----------------------------------------------------------------------------
function OnboardingPanel(props) {
  return <ChecklistPanel {...props} kind="onboarding" title="Onboarding" startCap="manage_onboarding" viewCap="view_employees" />;
}

function OffboardingPanel(props) {
  return <ChecklistPanel {...props} kind="offboarding" title="Offboarding" startCap="manage_offboarding" viewCap="view_employees" />;
}

function ChecklistPanel({ employeeId, onDone, kind, title, startCap }) {
  const { user } = useAuth();
  const canManage = hasCapability(user, startCap);
  const canInvite = kind === "onboarding" && hasCapability(user, "create_employee");
  const [ob, setOb] = useState(null);      // active instance
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [startForm, setStartForm] = useState({});
  const [invite, setInvite] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteLink, setInviteLink] = useState("");

  const load = async () => {
    setLoading(true); setNotFound(false);
    try {
      const { data } = await api.get(`/people/employees/${employeeId}/${kind}`);
      setOb(data);
    } catch (e) {
      if (e.response?.status === 404) setNotFound(true);
      else toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setLoading(false); }
    if (kind === "onboarding" && canInvite) {
      try {
        const { data } = await api.get(`/people/employees/${employeeId}/onboarding-invite`);
        setInvite(data.invite || null);
      } catch { /* ignore */ }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [employeeId, kind]);

  const sendInvite = async () => {
    try {
      const { data } = await api.post(`/people/employees/${employeeId}/onboarding-invite`, {
        expires_days: 7,
        send_email: true,
        base_url: window.location.origin,
      });
      setInviteLink(data.link);
      toast.success(data.emailed ? "Link emailed to candidate" : "Link created (candidate has no personal email — share manually)");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const startIt = async () => {
    try {
      await api.post(`/people/employees/${employeeId}/${kind}`, startForm);
      toast.success(`${title} started`);
      setStartOpen(false); setStartForm({});
      load(); onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const updateTask = async (t, patch) => {
    try {
      await api.patch(`/people/employees/${employeeId}/${kind}/tasks/${t.id}`, patch);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const completeIt = async () => {
    if (!window.confirm(`Complete ${title.toLowerCase()}? This finalizes the checklist.`)) return;
    try {
      const { data } = await api.post(`/people/employees/${employeeId}/${kind}/complete`);
      toast.success(`${title} completed`);
      if (data.assets_pending > 0) toast.warning(`${data.assets_pending} asset(s) still pending return`);
      load(); onDone?.();
    } catch (e) {
      const det = e.response?.data?.detail;
      if (det && typeof det === "object" && det.code === "mandatory_pending") {
        toast.error(`${det.tasks.length} mandatory task(s) pending: ${det.tasks.join(", ")}`);
      } else toast.error(formatApiError(det) || "Failed");
    }
  };

  if (loading) return <div className="text-slate-400 text-sm">Loading…</div>;

  if (notFound) {
    return (
      <Card>
        <CardContent className="text-center py-14">
          {kind === "onboarding" ? <ClipboardCheck className="w-10 h-10 mx-auto mb-2 text-slate-300" /> : <LogOutIcon className="w-10 h-10 mx-auto mb-2 text-slate-300" />}
          <p className="text-slate-500 text-sm mb-4">No {title.toLowerCase()} in progress for this employee.</p>
          {kind === "onboarding" && invite && (
            <div className="inline-block rounded-md border border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-600 mb-4" data-testid="invite-status">
              Onboarding form link {invite.submitted_at ? (
                <b className="text-emerald-700">submitted {formatDateTime(invite.submitted_at)}</b>
              ) : invite.expires_at < new Date().toISOString() ? (
                <b className="text-rose-600">expired</b>
              ) : (
                <b className="text-blue-700">awaiting candidate (expires {formatDateTime(invite.expires_at)})</b>
              )}
            </div>
          )}
          <div className="flex items-center justify-center gap-2 flex-wrap">
            {canManage && (
              <Button className="bg-[#7C1D3A] hover:bg-[#6c1c36]" onClick={() => setStartOpen(true)} data-testid={`start-${kind}-btn`}>
                <PlayCircle className="w-4 h-4 mr-2" /> Start {title}
              </Button>
            )}
            {canInvite && (
              <Button variant="outline" onClick={() => { setInviteLink(""); setInviteOpen(true); }} data-testid="send-invite-btn">
                <Send className="w-4 h-4 mr-2" /> Send Onboarding Form
              </Button>
            )}
          </div>
          <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
            <DialogContent data-testid="invite-dialog">
              <DialogHeader><DialogTitle>Send Onboarding Form to Candidate</DialogTitle></DialogHeader>
              <div className="space-y-3 text-sm">
                <p className="text-xs text-slate-600">
                  Generates a secure, single-use link (valid 7 days) the candidate opens <b>without a login</b> to fill in
                  their personal, statutory and bank details and upload documents. Their submission lands in the
                  <b> Approval Queue</b> for HR review — the master record is never touched directly.
                </p>
                {!inviteLink ? (
                  <Button onClick={sendInvite} className="bg-[#7C1D3A] hover:bg-[#6c1c36] w-full" data-testid="send-invite-confirm">
                    <Send className="w-4 h-4 mr-2" /> Create link & email candidate
                  </Button>
                ) : (
                  <div className="rounded border border-emerald-200 bg-emerald-50 p-3 text-xs space-y-2">
                    <div className="font-semibold text-emerald-800">Link ready</div>
                    <div className="break-all font-mono bg-white border border-emerald-200 rounded px-2 py-1.5" data-testid="invite-link">{inviteLink}</div>
                    <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(inviteLink); toast.success("Copied"); }}>
                      Copy link
                    </Button>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setInviteOpen(false)}>Close</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={startOpen} onOpenChange={setStartOpen}>
            <DialogContent data-testid={`start-${kind}-dialog`}>
              <DialogHeader><DialogTitle>Start {title}</DialogTitle></DialogHeader>
              <div className="space-y-3 text-sm">
                {kind === "onboarding" ? (
                  <Field2 label="Expected joining date">
                    <input type="date" value={startForm.expected_join_date || ""} onChange={(e) => setStartForm({ ...startForm, expected_join_date: e.target.value })} className="input" data-testid="ob-join-date" />
                  </Field2>
                ) : (
                  <>
                    <Field2 label="Last working day">
                      <input type="date" value={startForm.last_working_day || ""} onChange={(e) => setStartForm({ ...startForm, last_working_day: e.target.value })} className="input" data-testid="off-lwd" />
                    </Field2>
                    <Field2 label="Exit type">
                      <select value={startForm.exit_type || "resignation"} onChange={(e) => setStartForm({ ...startForm, exit_type: e.target.value })} className="input" data-testid="off-type">
                        <option value="resignation">Resignation</option>
                        <option value="termination">Termination</option>
                        <option value="retirement">Retirement</option>
                        <option value="end_of_contract">End of contract</option>
                        <option value="other">Other</option>
                      </select>
                    </Field2>
                    <Field2 label="Exit reason" span2>
                      <textarea rows={2} value={startForm.exit_reason || ""} onChange={(e) => setStartForm({ ...startForm, exit_reason: e.target.value })} className="input" data-testid="off-reason" />
                    </Field2>
                  </>
                )}
                <Field2 label="Notes" span2>
                  <textarea rows={2} value={startForm.notes || ""} onChange={(e) => setStartForm({ ...startForm, notes: e.target.value })} className="input" />
                </Field2>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setStartOpen(false)}>Cancel</Button>
                <Button onClick={startIt} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid={`start-${kind}-confirm`}>Start</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    );
  }

  const completed = ob.tasks.filter((t) => t.status === "completed" || t.status === "waived").length;
  const total = ob.tasks.length;
  const pct = Math.round((completed / Math.max(1, total)) * 100);
  const done = ob.status === "completed";

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="text-base">{title} · <span className="capitalize text-slate-600 text-sm font-normal">{ob.status.replace(/_/g, " ")}</span></CardTitle>
            <p className="text-xs text-slate-500 mt-0.5">
              {kind === "onboarding" ? "Expected join" : "Last working day"}: <b>{formatDate(ob.expected_join_date || ob.last_working_day) || "—"}</b>
              {ob.exit_type && <> · Type: <b className="capitalize">{ob.exit_type.replace(/_/g, " ")}</b></>}
            </p>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-500 mb-1">{completed} / {total} tasks · {pct}%</div>
            <div className="h-1.5 w-40 bg-slate-100 rounded overflow-hidden">
              <div className={`h-full ${pct === 100 ? "bg-emerald-500" : "bg-[#7C1D3A]"}`} style={{ width: `${pct}%` }} />
            </div>
            {!done && canManage && (
              <Button size="sm" className="mt-2 bg-emerald-600 hover:bg-emerald-700" onClick={completeIt} data-testid={`complete-${kind}-btn`}>
                <CheckCircle2 className="w-4 h-4 mr-1" /> Complete {title}
              </Button>
            )}
          </div>
        </div>
        {kind === "offboarding" && (ob.assets_pending_return || []).length > 0 && (
          <div className="mt-3 rounded border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
            <div className="font-semibold mb-1"><Package className="w-3.5 h-3.5 inline mr-1" /> {ob.assets_pending_return.length} asset(s) pending return:</div>
            <ul className="pl-4 list-disc space-y-0.5">
              {ob.assets_pending_return.map((a) => <li key={a.id}><b>{a.asset_id}</b> — {a.name}</li>)}
            </ul>
          </div>
        )}
      </CardHeader>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-2">#</th>
              <th className="text-left px-4 py-2">Task</th>
              <th className="text-left px-4 py-2">Owner</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">Notes</th>
              <th className="text-right px-4 py-2"></th>
            </tr>
          </thead>
          <tbody data-testid={`${kind}-tasks`}>
            {ob.tasks.map((t, i) => (
              <tr key={t.id} className={`border-t border-slate-100 ${t.status === "completed" ? "bg-emerald-50/40" : t.status === "waived" ? "bg-slate-50/60" : ""}`}>
                <td className="px-4 py-2 text-xs text-slate-500">{i + 1}</td>
                <td className="px-4 py-2">
                  <div className="font-medium">{t.title}</div>
                  {t.mandatory && <Badge variant="outline" className="text-[9px] px-1 py-0 mt-0.5">Mandatory</Badge>}
                </td>
                <td className="px-4 py-2 text-xs text-slate-600 uppercase">{t.owner || "—"}</td>
                <td className="px-4 py-2">
                  <TaskStatusBadge status={t.status} />
                </td>
                <td className="px-4 py-2 text-xs text-slate-600">{t.notes || "—"}</td>
                <td className="px-4 py-2 text-right">
                  {!done && canManage && (
                    <div className="inline-flex gap-1">
                      {t.status !== "completed" && (
                        <Button variant="ghost" size="sm" className="text-emerald-600 hover:text-emerald-700 text-xs" onClick={() => updateTask(t, { status: "completed" })} data-testid={`t-done-${t.key}`}>
                          <CheckCircle2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {t.status !== "waived" && !t.mandatory ? null : (
                        t.status !== "waived" && (
                          <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-700 text-xs" onClick={() => {
                            const notes = window.prompt("Reason for waiver?") || "";
                            if (notes) updateTask(t, { status: "waived", notes });
                          }} data-testid={`t-waive-${t.key}`}>Waive</Button>
                        )
                      )}
                      {t.status !== "pending" && (
                        <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-700 text-xs" onClick={() => updateTask(t, { status: "pending" })} data-testid={`t-reopen-${t.key}`}>Reopen</Button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function TaskStatusBadge({ status }) {
  const map = {
    pending: { label: "Pending", tint: "bg-slate-100 text-slate-600" },
    in_progress: { label: "In progress", tint: "bg-blue-100 text-blue-700" },
    completed: { label: "Done", tint: "bg-emerald-100 text-emerald-700" },
    waived: { label: "Waived", tint: "bg-slate-200 text-slate-600" },
    blocked: { label: "Blocked", tint: "bg-rose-100 text-rose-700" },
  };
  const m = map[status] || map.pending;
  return <Badge className={`${m.tint} capitalize`}>{m.label}</Badge>;
}


// -----------------------------------------------------------------------------
// Salary panel (Sep 2026) — visible only to view_employee_salary holders
// (HR Manager / Admin / Admin_addl). Full history grouped by FY, mid-year
// revisions via effective_from. Employees see only their current FY on
// My Profile. basic + hra must equal gross exactly (enforced client + server).
// -----------------------------------------------------------------------------
function _currentFY() {
  const d = new Date();
  const start = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1; // April–March
  return `${start}-${start + 1}`;
}

function SalaryPanel({ employeeId, canManage }) {
  const [records, setRecords] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    financial_year: _currentFY(), gross_salary: "", basic: "", hra: "",
    effective_from: "", revision_note: "",
  });

  const load = async () => {
    try {
      const { data } = await api.get(`/people/employees/${employeeId}/salary`);
      setRecords(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load salary"); setRecords([]); }
  };
  useEffect(() => { load(); }, [employeeId]);

  const gross = parseFloat(form.gross_salary) || 0;
  const basic = parseFloat(form.basic) || 0;
  const hra = parseFloat(form.hra) || 0;
  // Sep 2026 — breakup optional: blank Basic & HRA = gross-only record,
  // backfilled later via the same FY + effective_from.
  const oneSided = (form.basic === "") !== (form.hra === "");
  const sumMismatch = !oneSided && form.gross_salary !== "" && form.basic !== "" && Math.abs(basic + hra - gross) > 0.01;

  const save = async () => {
    if (!gross || gross <= 0) return toast.error("Gross salary is required");
    if (oneSided) return toast.error("Provide both Basic and HRA, or leave both blank for a gross-only record");
    if (sumMismatch) return toast.error("Basic + HRA must equal Gross Salary exactly");
    setSaving(true);
    try {
      await api.put(`/people/employees/${employeeId}/salary`, {
        financial_year: form.financial_year.trim(),
        gross_salary: gross,
        basic: form.basic === "" ? null : basic,
        hra: form.hra === "" ? null : hra,
        effective_from: form.effective_from || null,
        revision_note: form.revision_note || null,
      });
      toast.success("Salary revision recorded");
      setOpen(false);
      setForm({ financial_year: _currentFY(), gross_salary: "", basic: "", hra: "", effective_from: "", revision_note: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to save"); }
    finally { setSaving(false); }
  };

  // group by FY (already sorted desc from backend)
  const byFY = {};
  for (const r of records || []) (byFY[r.financial_year] ||= []).push(r);

  return (
    <Card data-testid="salary-panel"><CardContent className="pt-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
          <IndianRupee className="w-4 h-4 text-[#7C1D3A]" /> Salary history
        </div>
        {canManage && (
          <Button size="sm" className="bg-[#7C1D3A] hover:bg-[#6c1c36]" onClick={() => setOpen(true)} data-testid="salary-add-btn">
            Add / Revise
          </Button>
        )}
      </div>

      {records === null ? (
        <div className="text-sm text-slate-400 py-4">Loading…</div>
      ) : records.length === 0 ? (
        <div className="text-sm text-slate-400 py-4" data-testid="salary-empty">No salary records yet.</div>
      ) : (
        Object.entries(byFY).map(([fy, recs]) => (
          <div key={fy} className="rounded-lg border border-slate-200 overflow-hidden" data-testid={`salary-fy-${fy}`}>
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-700">
              FY {fy} <span className="font-normal text-slate-400">(April–March)</span>
            </div>
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                <tr className="border-b border-slate-200">
                  <th rowSpan={2} className="text-left px-3 py-1.5 align-bottom">Effective From</th>
                  <th colSpan={3} className="text-center px-3 py-1 text-[#7C1D3A] border-l border-slate-200">Monthly</th>
                  <th colSpan={3} className="text-center px-3 py-1 text-[#7C1D3A] border-l border-slate-200">Annually</th>
                  <th rowSpan={2} className="text-left px-3 py-1.5 align-bottom">Note</th>
                  <th rowSpan={2} className="text-left px-3 py-1.5 align-bottom">Updated</th>
                </tr>
                <tr>
                  <th className="text-right px-3 py-1.5 border-l border-slate-200">Basic</th>
                  <th className="text-right px-3 py-1.5">HRA</th>
                  <th className="text-right px-3 py-1.5">Gross</th>
                  <th className="text-right px-3 py-1.5 border-l border-slate-200">Basic</th>
                  <th className="text-right px-3 py-1.5">HRA</th>
                  <th className="text-right px-3 py-1.5">Gross</th>
                </tr>
              </thead>
              <tbody>
                {recs.map((r, i) => (
                  <tr key={r.id} className={`border-t border-slate-100 ${i === 0 ? "bg-emerald-50/40" : ""}`} data-testid={`salary-row-${r.id}`}>
                    <td className="px-3 py-2 font-medium text-slate-900 whitespace-nowrap">
                      {formatDate(r.effective_from)}
                      {i === 0 && <Badge className="ml-2 bg-emerald-100 text-emerald-700 text-[9px] px-1 py-0">LATEST</Badge>}
                      {r.breakup_pending && <Badge className="ml-2 bg-amber-100 text-amber-800 text-[9px] px-1 py-0" data-testid={`breakup-pending-${r.id}`}>BREAKUP PENDING</Badge>}
                    </td>
                    <td className="px-3 py-2 text-right">{r.basic != null ? formatINR(Math.round(r.basic / 12)) : <span className="text-xs text-slate-400 italic">pending</span>}</td>
                    <td className="px-3 py-2 text-right">{r.hra != null ? formatINR(Math.round(r.hra / 12)) : <span className="text-xs text-slate-400 italic">pending</span>}</td>
                    <td className="px-3 py-2 text-right">{formatINR(Math.round(r.gross_salary / 12))}</td>
                    <td className="px-3 py-2 text-right border-l border-slate-100">{r.basic != null ? formatINR(r.basic) : <span className="text-xs text-slate-400 italic">pending</span>}</td>
                    <td className="px-3 py-2 text-right">{r.hra != null ? formatINR(r.hra) : <span className="text-xs text-slate-400 italic">pending</span>}</td>
                    <td className="px-3 py-2 text-right font-semibold">{formatINR(r.gross_salary)}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{r.revision_note || "—"}</td>
                    <td className="px-3 py-2 text-[11px] text-slate-400 whitespace-nowrap">{r.updated_by_name || "—"} · {formatDateTime(r.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="salary-dialog">
          <DialogHeader><DialogTitle>Record salary revision</DialogTitle></DialogHeader>
          <p className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded px-2.5 py-1.5">
            Only Gross is mandatory. Leave <b>Basic</b> &amp; <b>HRA</b> blank to record a gross-only salary now —
            backfill the breakup later by saving again with the same FY + effective date (or via Salary Upload).
          </p>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <Label className="text-[10px] uppercase font-semibold text-slate-500">Financial Year *</Label>
              <Input className="mt-1" value={form.financial_year} onChange={(e) => setForm({ ...form, financial_year: e.target.value })} placeholder="2026-2027" data-testid="salary-fy" />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-semibold text-slate-500">Effective From</Label>
              <Input className="mt-1" type="date" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} data-testid="salary-effective" />
              <p className="text-[10px] text-slate-400 mt-0.5">Blank = 1 April of the FY</p>
            </div>
            <div>
              <Label className="text-[10px] uppercase font-semibold text-slate-500">Gross — Annual *</Label>
              <Input className="mt-1" type="number" min="0" value={form.gross_salary} onChange={(e) => setForm({ ...form, gross_salary: e.target.value })} data-testid="salary-gross" />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-semibold text-slate-500">Basic — Annual</Label>
              <Input className="mt-1" type="number" min="0" value={form.basic} onChange={(e) => setForm({ ...form, basic: e.target.value })} data-testid="salary-basic" />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-semibold text-slate-500">HRA — Annual</Label>
              <Input className="mt-1" type="number" min="0" value={form.hra} onChange={(e) => setForm({ ...form, hra: e.target.value })} data-testid="salary-hra" />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-semibold text-slate-500">Revision Note</Label>
              <Input className="mt-1" value={form.revision_note} onChange={(e) => setForm({ ...form, revision_note: e.target.value })} placeholder="e.g. Annual appraisal" data-testid="salary-note" />
            </div>
          </div>
          {sumMismatch && (
            <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded px-3 py-2" data-testid="salary-sum-error">
              Basic + HRA ({formatINR(basic + hra)}) must equal Gross Salary ({formatINR(gross)}) exactly.
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || sumMismatch} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="salary-save-btn">
              {saving ? "Saving…" : "Save Revision"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CardContent></Card>
  );
}

// -----------------------------------------------------------------------------
// Document types manager (Sep 2026) — Admin/Admin_addl only. Confidentiality is
// a TYPE-level setting (management choice): documents uploaded under a
// confidential type are viewable only by the employee, HR Manager, Admin and
// Admin_addl. Deleting a type reassigns its documents to the protected "other".
// -----------------------------------------------------------------------------
